/**
 * FunctionalPerformanceReports — department / function-level talent report.
 *
 * Shows aggregated competency evaluation scores per department, drillable
 * down to individual roles. Accessible to roles with `functional:tdp:own`.
 * Supports period (quarterly) and department filtering, and table/chart views.
 */
import React, { useState, useEffect } from 'react';
import { Table, BarChart3, ArrowLeft, TrendingUp, TrendingDown, Minus, AlertCircle, Layers, ChevronRight } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import './FunctionalPerformanceReports.css';

const STANDARD_PERIODS = [
  'Q1: January – March',
  'Q2: April – June',
  'Q3: July – September',
  'Q4: October – December',
];

const FunctionalPerformanceReports = () => {
  const { organizationId } = useAuth();
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(STANDARD_PERIODS[0]);
  const [competencyProfile, setCompetencyProfile] = useState('core');
  const [viewMode, setViewMode] = useState('groups');       // 'groups' | 'competencies'
  const [displayMode, setDisplayMode] = useState('table');  // 'table' | 'visual'
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [departments, setDepartments] = useState([]);
  const [periods, setPeriods] = useState(STANDARD_PERIODS);
  const [groupsData, setGroupsData] = useState([]);
  const [competenciesData, setCompetenciesData] = useState([]);
  const [expectedScore, setExpectedScore] = useState(80);

  const [filtersLoading, setFiltersLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Fetch filter options when org is known ──────────────────────────────
  useEffect(() => {
    if (organizationId) fetchFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // ── Re-fetch groups when dept / period / profile changes ─────────────────
  useEffect(() => {
    if (selectedDept && selectedPeriod) {
      fetchGroupsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDept, selectedPeriod, competencyProfile]);

  // ── Fetch competencies when a group is selected ──────────────────────────
  useEffect(() => {
    if (selectedGroup && selectedDept && selectedPeriod) {
      fetchCompetenciesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  const getEvalType = (profile = competencyProfile) =>
    profile === 'core' ? 'Core Competency Evaluation' : 'Technical Competency Evaluation';

  // ── Filters ──────────────────────────────────────────────────────────────
  const fetchFilters = async () => {
    setFiltersLoading(true);
    try {
      const [periodsRes, deptsRes] = await Promise.all([
        supabase.from('evaluations').select('evaluation_period').order('evaluation_period'),
        supabase.from('department').select('department_id, depart_name').eq('organization_id', organizationId).order('depart_name'),
      ]);
      if (periodsRes.error) throw periodsRes.error;
      if (deptsRes.error) throw deptsRes.error;

      const dbPeriods = (periodsRes.data || []).map(e => e.evaluation_period);
      const extras = dbPeriods.filter(p => !STANDARD_PERIODS.includes(p));
      setPeriods([...STANDARD_PERIODS, ...new Set(extras)]);

      const depts = deptsRes.data || [];
      setDepartments(depts);
      if (depts.length > 0) setSelectedDept(depts[0]);
    } catch (err) {
      setError('Failed to load filters. Please check your connection.');
      console.error(err);
    } finally {
      setFiltersLoading(false);
    }
  };

  // ── Groups data ──────────────────────────────────────────────────────────
  const fetchGroupsData = async () => {
    setLoading(true);
    setError(null);
    setGroupsData([]);
    setViewMode('groups');
    setSelectedGroup(null);
    setCompetenciesData([]);

    try {
      const groupType = competencyProfile === 'core' ? 'Core' : 'Technical';

      const { data: groups, error: groupsErr } = await supabase
        .from('competency_groups')
        .select('group_id, group_name, group_weight, display_order')
        .eq('group_type', groupType)
        .or(`department_id.is.null,department_id.eq.${selectedDept.department_id}`)
        .order('display_order', { ascending: true, nullsFirst: false });

      if (groupsErr) throw groupsErr;
      if (!groups || groups.length === 0) {
        setGroupsData([]);
        setLoading(false);
        return;
      }

      // Fetch completed evaluations for this dept + period + type
      const { data: evaluations, error: evalErr } = await supabase
        .from('evaluations')
        .select('evaluation_id')
        .eq('department_id', selectedDept.department_id)
        .eq('evaluation_period', selectedPeriod)
        .eq('evaluation_type', getEvalType())
        .eq('status', 'Complete');

      if (evalErr) throw evalErr;

      // Fetch target score
      const { data: target } = await supabase
        .from('performance_targets')
        .select('target_score')
        .eq('department_id', selectedDept.department_id)
        .eq('evaluation_period', selectedPeriod)
        .eq('evaluation_type', getEvalType())
        .maybeSingle();

      const expected = target?.target_score ? Number(target.target_score) : 80;
      setExpectedScore(expected);

      if (!evaluations || evaluations.length === 0) {
        setGroupsData(groups.map(g => ({
          group_id: g.group_id,
          name: g.group_name,
          weight: g.group_weight,
          expected,
          actual: null,
          variance: null,
        })));
        setLoading(false);
        return;
      }

      const evalIds = evaluations.map(e => e.evaluation_id);
      const groupIds = groups.map(g => g.group_id);

      const { data: ratings, error: ratingsErr } = await supabase
        .from('evaluation_ratings')
        .select('group_id, rating')
        .in('evaluation_id', evalIds)
        .in('group_id', groupIds);

      if (ratingsErr) throw ratingsErr;

      const aggMap = {};
      (ratings || []).forEach(r => {
        if (r.rating == null) return;
        if (!aggMap[r.group_id]) aggMap[r.group_id] = { sum: 0, count: 0 };
        aggMap[r.group_id].sum += r.rating;
        aggMap[r.group_id].count += 1;
      });

      setGroupsData(groups.map(g => {
        const agg = aggMap[g.group_id];
        const actual = agg ? Math.round((agg.sum / (agg.count * 5)) * 100) : null;
        return {
          group_id: g.group_id,
          name: g.group_name,
          weight: g.group_weight,
          expected,
          actual,
          variance: actual !== null ? actual - expected : null,
        };
      }));
    } catch (err) {
      setError('Failed to load competency groups.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Competencies drill-down ──────────────────────────────────────────────
  const fetchCompetenciesData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: competencies, error: compErr } = await supabase
        .from('competencies')
        .select('competency_id, competency_name, competency_weight, display_order')
        .eq('group_id', selectedGroup.group_id)
        .order('display_order', { ascending: true, nullsFirst: false });

      if (compErr) throw compErr;
      if (!competencies || competencies.length === 0) {
        setCompetenciesData([]);
        setLoading(false);
        return;
      }

      const { data: evaluations } = await supabase
        .from('evaluations')
        .select('evaluation_id')
        .eq('department_id', selectedDept.department_id)
        .eq('evaluation_period', selectedPeriod)
        .eq('evaluation_type', getEvalType())
        .eq('status', 'Complete');

      if (!evaluations || evaluations.length === 0) {
        setCompetenciesData(competencies.map(c => ({
          competency_id: c.competency_id,
          name: c.competency_name,
          weight: c.competency_weight,
          expected: expectedScore,
          actual: null,
          variance: null,
        })));
        setLoading(false);
        return;
      }

      const evalIds = evaluations.map(e => e.evaluation_id);
      const compIds = competencies.map(c => c.competency_id);

      const { data: ratings, error: ratingsErr } = await supabase
        .from('evaluation_ratings')
        .select('competency_id, rating')
        .in('evaluation_id', evalIds)
        .in('competency_id', compIds);

      if (ratingsErr) throw ratingsErr;

      const aggMap = {};
      (ratings || []).forEach(r => {
        if (r.rating == null) return;
        if (!aggMap[r.competency_id]) aggMap[r.competency_id] = { sum: 0, count: 0 };
        aggMap[r.competency_id].sum += r.rating;
        aggMap[r.competency_id].count += 1;
      });

      setCompetenciesData(competencies.map(c => {
        const agg = aggMap[c.competency_id];
        const actual = agg ? Math.round((agg.sum / (agg.count * 5)) * 100) : null;
        return {
          competency_id: c.competency_id,
          name: c.competency_name,
          weight: c.competency_weight,
          expected: expectedScore,
          actual,
          variance: actual !== null ? actual - expectedScore : null,
        };
      }));
    } catch (err) {
      setError('Failed to load competencies.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleDrillDown = (group) => {
    setSelectedGroup(group);
    setViewMode('competencies');
  };

  const handleBackToGroups = () => {
    setSelectedGroup(null);
    setViewMode('groups');
    setCompetenciesData([]);
  };

  const handleProfileChange = (profile) => {
    setCompetencyProfile(profile);
    handleBackToGroups();
  };

  // ── Derived display data ─────────────────────────────────────────────────
  const currentData = viewMode === 'groups' ? groupsData : competenciesData;
  const dataWithActual = currentData.filter(d => d.actual !== null);
  const avgActual = dataWithActual.length > 0
    ? Math.round(dataWithActual.reduce((s, d) => s + d.actual, 0) / dataWithActual.length)
    : null;

  const getVarianceIcon = (v) => {
    if (v === null) return <Minus size={13} />;
    if (v > 0) return <TrendingUp size={13} />;
    if (v < 0) return <TrendingDown size={13} />;
    return <Minus size={13} />;
  };

  const formatVariance = (v) => {
    if (v === null) return '—';
    return `${v > 0 ? '+' : ''}${v}%`;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fn-container">
      {/* Header */}
      <div className="fn-header">
        <h2 className="fn-title"><i className="bi bi-diagram-3" />Functional Performance Report</h2>
        <p className="fn-subtitle">
          Analyze functional competencies with a detailed breakdown of performance metrics and variances by department and evaluation period.
        </p>
        <hr className="fn-divider" />
      </div>

      {/* Filters */}
      <div className="fn-filters">
        <div className="fn-filter-group">
          <label className="fn-filter-label">Function / Department</label>
          {filtersLoading ? (
            <div className="fn-filter-skeleton" />
          ) : (
            <select
              className="fn-filter-select"
              value={selectedDept?.department_id || ''}
              onChange={(e) => {
                const dept = departments.find(d => d.department_id === Number(e.target.value));
                setSelectedDept(dept || null);
              }}
            >
              {departments.length === 0 && <option value="">No departments available</option>}
              {departments.map(d => (
                <option key={d.department_id} value={d.department_id}>{d.depart_name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="fn-filter-group">
          <label className="fn-filter-label">Evaluation Period</label>
          {filtersLoading ? (
            <div className="fn-filter-skeleton" />
          ) : (
            <select
              className="fn-filter-select"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              {periods.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Competency Profile Tabs */}
      <div className="fn-tabs">
        <button
          className={`fn-tab ${competencyProfile === 'core' ? 'fn-tab-active' : ''}`}
          onClick={() => handleProfileChange('core')}
        >
          Core Competencies
        </button>
        <button
          className={`fn-tab ${competencyProfile === 'technical' ? 'fn-tab-active' : ''}`}
          onClick={() => handleProfileChange('technical')}
        >
          Technical Competencies
        </button>
      </div>

      {/* Summary strip */}
      {!filtersLoading && selectedDept && (
        <div className="fn-summary-strip">
          <div className="fn-summary-item">
            <span className="fn-summary-label">Department</span>
            <span className="fn-summary-val">{selectedDept.depart_name}</span>
          </div>
          <div className="fn-summary-sep" />
          <div className="fn-summary-item">
            <span className="fn-summary-label">Period</span>
            <span className="fn-summary-val">{selectedPeriod}</span>
          </div>
          <div className="fn-summary-sep" />
          <div className="fn-summary-item">
            <span className="fn-summary-label">Target Score</span>
            <span className="fn-summary-val">{expectedScore}%</span>
          </div>
          <div className="fn-summary-sep" />
          <div className="fn-summary-item">
            <span className="fn-summary-label">
              {viewMode === 'groups' ? 'Groups Avg' : 'Competencies Avg'}
            </span>
            <span className={`fn-summary-val ${avgActual !== null ? (avgActual >= expectedScore ? 'fn-score-pos' : 'fn-score-neg') : ''}`}>
              {avgActual !== null ? `${avgActual}%` : '—'}
            </span>
          </div>
        </div>
      )}

      {/* Toolbar: toggle + breadcrumb */}
      <div className="fn-toolbar">
        <div className="fn-view-toggle">
          <button
            className={`fn-toggle-btn ${displayMode === 'table' ? 'fn-toggle-active' : ''}`}
            onClick={() => setDisplayMode('table')}
          >
            <Table size={15} />
            <span>Table View</span>
          </button>
          <button
            className={`fn-toggle-btn ${displayMode === 'visual' ? 'fn-toggle-active' : ''}`}
            onClick={() => setDisplayMode('visual')}
          >
            <BarChart3 size={15} />
            <span>Visual Report</span>
          </button>
        </div>

        {viewMode === 'competencies' && (
          <div className="fn-breadcrumb">
            <button className="fn-back-btn" onClick={handleBackToGroups}>
              <ArrowLeft size={14} />
              <span>All Groups</span>
            </button>
            <ChevronRight size={13} className="fn-breadcrumb-sep" />
            <span className="fn-breadcrumb-current">{selectedGroup?.name}</span>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="fn-loading">
          <div className="fn-spinner" />
          <span>Loading data...</span>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="fn-error">
          <AlertCircle size={17} />
          <span>{error}</span>
        </div>
      )}

      {/* ── Table View ─────────────────────────────────────────────────── */}
      {!loading && !error && displayMode === 'table' && (
        <div className="fn-table-wrapper">
          {currentData.length > 0 ? (
            <table className="fn-table">
              <thead>
                <tr>
                  <th className="fn-th fn-th-name">
                    {viewMode === 'groups' ? 'Competency Group' : 'Competency'}
                  </th>
                  <th className="fn-th">Weighting</th>
                  <th className="fn-th">Expected</th>
                  <th className="fn-th fn-th-actual">Actual</th>
                  <th className="fn-th">Variance</th>
                  {viewMode === 'groups' && <th className="fn-th">Details</th>}
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, idx) => (
                  <tr
                    key={item.group_id ?? item.competency_id}
                    className={idx % 2 === 0 ? 'fn-row-even' : 'fn-row-odd'}
                  >
                    <td className="fn-td fn-td-name">{item.name}</td>
                    <td className="fn-td fn-td-center">
                      {item.weight != null ? `${item.weight}%` : '—'}
                    </td>
                    <td className="fn-td fn-td-center">{item.expected}%</td>
                    <td className="fn-td fn-td-actual">
                      {item.actual !== null ? (
                        <div className="fn-actual-cell">
                          <span className="fn-actual-val">{item.actual}%</span>
                          <div className="fn-mini-track">
                            <div
                              className={`fn-mini-fill ${competencyProfile === 'core' ? 'fn-mini-core' : 'fn-mini-tech'}`}
                              style={{ width: `${item.actual}%` }}
                            />
                          </div>
                        </div>
                      ) : <span className="fn-null">—</span>}
                    </td>
                    <td className="fn-td fn-td-center">
                      {item.variance !== null ? (
                        <span className={`fn-variance-pill ${item.variance >= 0 ? 'fn-pill-pos' : 'fn-pill-neg'}`}>
                          {getVarianceIcon(item.variance)}
                          {formatVariance(item.variance)}
                        </span>
                      ) : <span className="fn-null">—</span>}
                    </td>
                    {viewMode === 'groups' && (
                      <td className="fn-td fn-td-center">
                        <button
                          className="fn-drill-btn"
                          onClick={() => handleDrillDown(item)}
                        >
                          View Competencies
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="fn-empty">
              <Layers size={44} className="fn-empty-icon" />
              <p className="fn-empty-title">No data available</p>
              <p className="fn-empty-sub">
                No completed evaluations found for this department and period.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Visual Report ──────────────────────────────────────────────── */}
      {!loading && !error && displayMode === 'visual' && (
        <div className="fn-visual-grid">
          {currentData.length > 0 ? (
            currentData.map(item => (
              <div key={item.group_id ?? item.competency_id} className="fn-visual-card">
                <div className="fn-visual-card-head">
                  <h3 className="fn-visual-card-title">{item.name}</h3>
                  <div className="fn-visual-card-meta">
                    {item.weight != null && (
                      <span className="fn-weight-badge">Wt: {item.weight}%</span>
                    )}
                    {item.variance !== null && (
                      <span className={`fn-variance-badge ${item.variance >= 0 ? 'fn-vbadge-pos' : 'fn-vbadge-neg'}`}>
                        {getVarianceIcon(item.variance)}
                        {formatVariance(item.variance)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="fn-visual-card-body">
                  <div className="fn-bar-row">
                    <span className="fn-bar-label">Expected</span>
                    <div className="fn-bar-track">
                      <div
                        className="fn-bar-fill fn-bar-expected"
                        style={{ width: `${item.expected}%` }}
                      />
                    </div>
                    <span className="fn-bar-pct">{item.expected}%</span>
                  </div>

                  <div className="fn-bar-row">
                    <span className="fn-bar-label">Actual</span>
                    <div className="fn-bar-track">
                      {item.actual !== null ? (
                        <div
                          className={`fn-bar-fill ${competencyProfile === 'core' ? 'fn-bar-core' : 'fn-bar-tech'}`}
                          style={{ width: `${item.actual}%` }}
                        />
                      ) : (
                        <span className="fn-bar-nodata">No data</span>
                      )}
                    </div>
                    <span className="fn-bar-pct">
                      {item.actual !== null ? `${item.actual}%` : '—'}
                    </span>
                  </div>

                  {viewMode === 'groups' && (
                    <button
                      className="fn-visual-drill-btn"
                      onClick={() => handleDrillDown(item)}
                    >
                      View Competencies
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="fn-empty">
              <Layers size={44} className="fn-empty-icon" />
              <p className="fn-empty-title">No data available</p>
              <p className="fn-empty-sub">
                No completed evaluations found for this department and period.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FunctionalPerformanceReports;
